@if (file) {
  <aems-mc-modal [open]="openFileViewer" (closed)="closeFileViewer()">
    <!-- Fixed Header -->
    <div modal-header>
      <span>{{ file.displayName }}</span>
      @if (showLinkFileIcon) {
        <div style="margin-top: 8px">
          <mc-checkbox
            [label]="linkFileMessage"
            [checked]="file.linkFile"
            fit="small"
            [disabled]="file.isLinkFileDisabled"
            (change)="onFileLinkedChange(file)"
            data-cy="is-linked-file"></mc-checkbox>
        </div>
      }
    </div>

    <!-- Scrollable Content Area -->
    @if (sanitizedUrl) {
      @if (isEmbeddableObject(file)) {
        <object [type]="file.fileType" [data]="sanitizedUrl" style="width: 100%; height: 100%">{{ file.displayName }}</object>
      } @else if (isImageFile(file)) {
        <div class="osd-wrapper">
          <div #osdContainer class="osd-container"></div>
          @if (annotationModeEnabled || annotations.length > 0) {
            <div class="annotation-panel">
              <div class="annotation-panel-title">Annotations</div>
              @if (annotations.length === 0) {
                <div class="annotation-panel-empty">Draw a rectangle or polygon on the image to add a label.</div>
              }
              @for (annotation of annotations; track annotation.id) {
                <div class="annotation-card">
                  <div class="annotation-card-header">
                    <span
                      class="annotation-color-swatch"
                      [style.background-color]="getAnnotationColor(annotation)"
                      [title]="isPolygonAnnotation(annotation) ? 'Polygon' : 'Rectangle'"></span>
                    <span class="annotation-card-label">{{ getAnnotationLabel(annotation) }}</span>
                    @if (isGSCUser) {
                      <mc-button
                        label="Remove annotation"
                        hiddenlabel
                        fit="small"
                        icon="trash"
                        appearance="danger"
                        variant="ghost"
                        (click)="deleteAnnotationEntry(annotation)"
                        (keydown.enter)="deleteAnnotationEntry(annotation)"></mc-button>
                    }
                  </div>
                  @for (field of annotationFields; track field.key) {
                    <mc-input
                      ngDefaultControl
                      hiddenlabel
                      [label]="field.placeholder"
                      fit="small"
                      [placeholder]="field.placeholder"
                      [attr.maxlength]="field.maxLength"
                      [value]="getAnnotationField(annotation, field.key)"
                      [attr.invalid]="isFieldInvalid(annotation, field.key) ? true : null"
                      [attr.disabled]="isGSCUser ? null : true"
                      oninput="this.value = this.value.toUpperCase()"
                      (input)="onAnnotationFieldInput(annotation, field.key, $event)"
                      [attr.data-cy]="'annotation-' + field.key + '-input'"></mc-input>
                  }
                </div>
              }
            </div>
          }
        </div>
      } @else {
        <img style="width: 100%; height: 100%; object-fit: contain" [src]="sanitizedUrl" [alt]="file.displayName" />
      }
    } @else {
      <div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center">
        <span>No preview available</span>
      </div>
    }

    <!-- Fixed Footer -->
    <div modal-footer style="display: flex; gap: 8px">
      <div style="width: 50px; text-align: center; display: flex; justify-content: center; align-items: center">{{ navDisplayText }}</div>
      <mc-button
        [disabled]="disablePrevious"
        (click)="onPreviousClicked()"
        (keydown.enter)="onPreviousClicked()"
        label="Button"
        hiddenlabel
        fit="medium"
        icon="arrow-left"
        appearance="neutral"
        variant="filled"></mc-button>
      <mc-button
        [disabled]="disableNext"
        (click)="onNextClicked()"
        (keydown.enter)="onNextClicked()"
        label="Button"
        hiddenlabel
        fit="medium"
        icon="arrow-right"
        appearance="neutral"
        variant="filled"></mc-button>
      @if (isImageFile(file)) {
        <mc-button
          (click)="zoomIn()"
          (keydown.enter)="zoomIn()"
          label="Zoom in"
          hiddenlabel
          fit="medium"
          icon="plus-circle"
          appearance="primary"
          variant="outlined"></mc-button>
        <mc-button
          (click)="resetZoom()"
          (keydown.enter)="resetZoom()"
          label="Reset zoom"
          hiddenlabel
          fit="medium"
          icon="house"
          appearance="primary"
          variant="outlined"></mc-button>
        <mc-button
          (click)="zoomOut()"
          (keydown.enter)="zoomOut()"
          label="Zoom out"
          hiddenlabel
          fit="medium"
          icon="minus-circle"
          appearance="primary"
          variant="outlined"></mc-button>
        @if (isGSCUser) {
          <mc-button
            [appearance]="annotationModeEnabled ? 'primary' : 'neutral'"
            [variant]="annotationModeEnabled ? 'filled' : 'outlined'"
            (click)="toggleAnnotationMode()"
            (keydown.enter)="toggleAnnotationMode()"
            label="Annotate"
            hiddenlabel
            fit="medium"
            icon="pencil"></mc-button>
        }
        @if (annotationModeEnabled) {
          <mc-button
            [appearance]="activeDrawingTool === 'rectangle' ? 'primary' : 'neutral'"
            [variant]="activeDrawingTool === 'rectangle' ? 'filled' : 'outlined'"
            (click)="setDrawingTool('rectangle')"
            (keydown.enter)="setDrawingTool('rectangle')"
            label="Rectangle"
            hiddenlabel
            fit="medium"
            icon="rectangle"></mc-button>
          <mc-button
            [appearance]="activeDrawingTool === 'polygon' ? 'primary' : 'neutral'"
            [variant]="activeDrawingTool === 'polygon' ? 'filled' : 'outlined'"
            (click)="setDrawingTool('polygon')"
            (keydown.enter)="setDrawingTool('polygon')"
            label="Polygon"
            hiddenlabel
            fit="medium"
            icon="shapes"></mc-button>
        }
        @if (annotationModeEnabled && annotations.length > 0) {
          <mc-button
            [disabled]="submittingAnnotations"
            [loading]="submittingAnnotations"
            (click)="submitAnnotations()"
            (keydown.enter)="submitAnnotations()"
            label="Submit"
            hiddenlabel
            fit="medium"
            icon="tray-arrow-up"
            appearance="neutral"
            variant="outlined"></mc-button>
        }
      }
    </div>
    @if (!hideActions) {
      <div modal-footer style="display: flex; gap: 8px">
        @if (!readOnly) {
          <mc-button
            [disabled]="file.isDeleteDisabled"
            label="Delete"
            [loading]="deleting"
            slot="secondaryAction"
            variant="filled"
            appearance="neutral"
            (click)="onDeleteFileClick()"
            (keydown.enter)="onDeleteFileClick()"
            fit="medium"></mc-button>
        }
        <mc-button
          [disabled]="file.isDownloadDisabled"
          fit="medium"
          icon="tray-arrow-down"
          label="Download"
          variant="filled"
          appearance="neutral"
          slot="secondaryAction"
          (click)="downloadFile(file)"
          (keydown.enter)="downloadFile(file)"
          >Download</mc-button
        >
        <mc-button
          label="Close"
          slot="primaryAction"
          appearance="primary"
          fit="medium"
          (click)="closeFileViewer()"
          (keydown.enter)="closeFileViewer()"></mc-button>
      </div>
    }
  </aems-mc-modal>
}
